import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '@gen2/hooks';
import { useSidebarStore } from '@gen2/hooks/stores';
import { Permissions } from '@gen2/utils/permissions/permissions';
import { useMemo } from 'react';
import { MenuItem, settings } from '../data';
import {
  StyledSidebarList,
  StyledSidebarListItem,
  StyledSidebarListItemLink,
  StyledSubMenuHeading,
  StyledSpan,
  StyledSvg,
} from '../sidebar.styled';

export const Settings = () => {
  const store = useSidebarStore();
  const { featureFlags, user } = useAuth();
  const onItemClick = (item: MenuItem) => {
    store.setOpen(false);

    if (item.id === 'home') return; // dashboard has no submenu

    store.setSelected(item);
    store.setSubMenuOpen(false);
  };

  const computedSettings = useMemo(() => {
    const currentOrgId = sessionStorage.getItem('FI-ORGANISATION-ID');
    const currentOrg = user?.organisations?.find(
      (org) => org.id === currentOrgId,
    );

    const masterFiltered = settings.filter((item) => {
      if (item.isMaster) {
        return currentOrg?.is_master;
      }
      return true;
    });

    // Filter out Usage & Billing if plan is null
    const planFiltered = masterFiltered.filter((item) => {
      if (item.id === 'usageAndBilling') {
        // Only show Usage & Billing if plan exists
        return !!currentOrg?.plan;
      }
      return true;
    });

    if (!featureFlags) {
      // Return only settings that don't have a featureFlag if flags are not loaded
      return planFiltered.filter((item) => !item.featureFlag);
    }

    return planFiltered.filter((item) => {
      if (!item.featureFlag) {
        // Always include items without a feature flag
        return true;
      }

      if (Array.isArray(item.featureFlag)) {
        // Include if *any* of the flags in the array are enabled
        return item.featureFlag.some(
          (flag) => featureFlags[flag as keyof typeof featureFlags],
        );
      } else {
        // Include if the single flag is enabled
        return featureFlags[item.featureFlag as keyof typeof featureFlags];
      }
    });
  }, [featureFlags, user]);

  return (
    <StyledSidebarList data-cy="sidebar-settings">
      <StyledSubMenuHeading>Settings</StyledSubMenuHeading>
      {computedSettings.map((item: MenuItem) => {
        const Item = (
          <StyledSidebarListItem key={item.title}>
            <StyledSidebarListItemLink
              onClick={() => onItemClick(item)}
              to={item.to || ''}
              data-cy={`sidebar-settings-link-${item?.title?.toLowerCase()}`}
            >
              <StyledSvg>
                <FontAwesomeIcon size="lg" icon={item.icon || 'x'} />
              </StyledSvg>
              <StyledSpan>{item.title}</StyledSpan>
            </StyledSidebarListItemLink>
          </StyledSidebarListItem>
        );

        if (item.permission) {
          return (
            <Permissions key={item.title} allow={item.permission}>
              {Item}
            </Permissions>
          );
        }

        return Item;
      })}
    </StyledSidebarList>
  );
};
